import type { CSSProperties } from 'react';
import { CachedImage } from './CachedImage';
import type { BrandSnapshot } from '../../types';

interface BrandLogoProps {
  brand: BrandSnapshot;
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  maxHeight?: string;
}

// Renders the brand logo in a corner with consistent margins. Hidden if
// the brand has no logo. The maxHeight is a vh-relative cap so the logo
// sizes appropriately across resolutions; templates should pass between
// 6vh and 10vh depending on layout density.
export function BrandLogo({
  brand,
  position = 'top-right',
  maxHeight = '7vh',
}: BrandLogoProps) {
  if (!brand.logo) return null;

  const cornerPos: Record<NonNullable<BrandLogoProps['position']>, CSSProperties> = {
    'top-left': { top: '2.5vh', left: '2.5vw' },
    'top-right': { top: '2.5vh', right: '2.5vw' },
    'bottom-left': { bottom: '2.5vh', left: '2.5vw' },
    'bottom-right': { bottom: '2.5vh', right: '2.5vw' },
  };

  const style: CSSProperties = {
    position: 'absolute',
    ...cornerPos[position],
    maxHeight,
    maxWidth: '22vw',
    objectFit: 'contain',
    zIndex: 5,
  };

  return <CachedImage mediaRef={brand.logo} style={style} alt="Brand logo" />;
}
